/* md-pdp.js: product page behavior (architecture.md 5.20, 5.12, 4.9). Loaded with defer by main-product only.
   <md-pdp-controller> (one per main-product render) owns, for its own product form only:
   1. Purchase option: shows the price lines ([data-md-plan-show]) matching the form's selling_plan; on a Dawn variant
      change it swaps the purchase options and the sticky bar from the same section response, keeps the chosen plan
      only if the new variant offers it, otherwise uses the server default (one-time, or the first plan when the
      product requires one) (CR-P-1, CR-P-3, CR-P-5). Reconciles again on bfcache restore (CR-P-8).
   2. Sticky bar: moved to <body>; shown (and focusable) only once the buy box button is above the viewport; inert and
      aria-hidden otherwise (CR-P-4). Add submits the main form with requestSubmit.
   3. One active controller per document: a newer one (Dawn product swap) tears the previous one down, including its
      sticky bar, listeners and subscription (CR-P-2).
   4. Accurate partial adds (FQC-1-2): before Dawn's product-form posts, a capture-phase submit hook reads /cart.js as
      a snapshot, then re-submits so Dawn's handler runs unchanged. On Dawn's cartError (a 422, e.g. more than stock)
      it re-reads /cart.js and sums every line of the variant before and after (an automatic discount can split one
      variant over several lines, so Shopify's own count in the 422 text can be wrong). When units did land it
      replaces Dawn's message with data-md-stock-partial ("Added {n}. ..."), publishes cartUpdate with the fresh cart
      (md-b2g1.js then refreshes the drawer and its meter) and refreshes the header cart bubble. Nothing landed or
      no snapshot: Dawn's message stays.
   Gallery edge tone (NATHAN-PDP-MOBILE-3): on phones each contained gallery photo sets --md-edge on its slide to the
   average color of its own left and right edges, so the 4:3 side bands match the packshot gray (no seam). Skipped for
   cover-filled photos, non-uniform edges, or when the canvas read is blocked (cross-origin): the CSS token stays.
   <md-ugc-rail> wires its video cards (poster first, sources on demand, one playing video per page).
   Never computes a price. Never runs inside Dawn's quick-add modal. */
if (!customElements.get('md-pdp-controller')) {
  let activeController = null;

  customElements.define(
    'md-pdp-controller',
    class MdPdpController extends HTMLElement {
      connectedCallback() {
        const root = this.closest('product-info');
        if (!root || this.closest('quick-add-modal') || root.dataset.originalSection) {
          this.remove(); // Dawn quick add injects product-info into a modal (quick-add.js); never run there
          return;
        }
        if (activeController && activeController !== this) activeController.deactivate();
        activeController = this;
        this.active = true;
        this.root = root;
        this.sectionId = this.dataset.sectionId;
        this.form = document.getElementById(this.dataset.formId);
        this.cleanups = [];
        this.sticky = root.querySelector('md-pdp-sticky');
        if (this.sticky) document.body.appendChild(this.sticky); // fixed bar never clipped by the product grid
        this.listen(document, 'change', (event) => this.onChange(event));
        this.listen(window, 'pageshow', () => this.reconcile());
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
          this.cleanups.push(unsubscribe);
          this.cleanups.push(subscribe(PUB_SUB_EVENTS.cartError, (data) => this.onCartError(data)));
          this.cleanups.push(
            subscribe(PUB_SUB_EVENTS.cartUpdate, (data) => {
              if (data && data.source === 'product-form') this.pending = null;
            })
          );
        }
        const host = this.form && this.form.closest('product-form');
        if (host) this.listen(host, 'submit', (event) => this.onSubmitCapture(event), true);
        this.seq = 0;
        this.initSticky();
        this.reconcile();
      }

      disconnectedCallback() {
        this.deactivate();
      }

      deactivate() {
        if (!this.active) return;
        this.active = false;
        this.cleanups.forEach((fn) => {
          try { fn(); } catch (e) { /* already gone */ }
        });
        this.cleanups = [];
        this.sticky?.remove();
        if (activeController === this) activeController = null;
      }

      listen(target, type, handler, options) {
        target.addEventListener(type, handler, options);
        this.cleanups.push(() => target.removeEventListener(type, handler, options));
      }

      planInputs() {
        if (!this.form) return [];
        return Array.from(this.form.elements).filter((el) => el.name === 'selling_plan' && el.type === 'radio');
      }

      selectedPlan() {
        const checked = this.planInputs().find((el) => el.checked);
        return checked ? checked.value : '';
      }

      applyPlan() {
        const plan = this.selectedPlan();
        [this.root, this.sticky].forEach((scope) => {
          scope?.querySelectorAll('[data-md-plan-show]').forEach((el) => {
            el.hidden = el.dataset.mdPlanShow !== plan;
          });
        });
      }

      syncStickyButton() {
        const main = this.form?.querySelector('[type="submit"]');
        const add = this.sticky?.querySelector('[data-md-sticky-add]');
        if (!main || !add) return;
        const disabled = main.disabled || main.getAttribute('aria-disabled') === 'true';
        add.disabled = disabled;
        add.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        const label = main.querySelector('span')?.textContent.trim();
        if (label) add.textContent = label;
      }

      reconcile() {
        if (!this.active) return;
        const inputs = this.planInputs();
        if (inputs.length && !inputs.some((el) => el.checked)) {
          const fallback = inputs.find((el) => el.hasAttribute('data-md-default')) || inputs[0];
          fallback.checked = true;
        }
        this.applyPlan();
        this.syncStickyButton();
      }

      onChange(event) {
        if (!this.active) return;
        const t = event.target;
        if (!(t instanceof HTMLInputElement) || t.form !== this.form || t.closest('quick-add-modal')) return;
        if (t.name === 'selling_plan') this.applyPlan();
        // Dawn sets id to '' and disables the button for an unavailable combination (product-info.js:187-192)
        if (t.name === 'id') setTimeout(() => this.syncStickyButton(), 0);
      }

      onVariantChange(event) {
        if (!this.active || event?.data?.sectionId !== this.sectionId) return;
        const html = event.data.html;
        const idInput = this.form?.querySelector('input[name="id"]');
        if (event.data.variant && idInput && String(event.data.variant.id) !== idInput.value) return; // not our form
        const previous = this.selectedPlan();
        if (html && typeof html.getElementById === 'function') {
          const swap = (id) => {
            const target = document.getElementById(id);
            const source = html.getElementById(id);
            if (target) target.innerHTML = source ? source.innerHTML : '';
          };
          swap(`md-purchase-${this.sectionId}`);
          swap(`md-sticky-dyn-${this.sectionId}`);
        }
        const keep = previous !== '' && this.planInputs().find((el) => el.value === previous);
        if (keep) keep.checked = true;
        this.reconcile();
      }

      cartUrl() {
        return (window.routes && window.routes.cart_url) || '/cart';
      }

      async readCart() {
        const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
        const timer = ctrl ? setTimeout(() => ctrl.abort(), 4000) : 0;
        try {
          const res = await fetch(`${this.cartUrl()}.js`, { headers: { Accept: 'application/json' }, signal: ctrl ? ctrl.signal : undefined });
          const cart = res.ok ? await res.json() : null;
          return cart && Array.isArray(cart.items) ? cart : null;
        } catch (e) {
          return null;
        } finally {
          clearTimeout(timer);
        }
      }

      // Units of one variant across every cart line (as MdCartAdd counts them).
      static units(cart, variantId) {
        return cart.items.reduce((sum, item) => sum + (String(item.variant_id) === String(variantId) ? item.quantity : 0), 0);
      }

      onSubmitCapture(event) {
        if (!this.active || event.target !== this.form) return;
        if (this.releasing) return; // our own re-submit: Dawn's handler takes it from here
        const button = this.form.querySelector('[type="submit"]');
        if (!button || button.getAttribute('aria-disabled') === 'true') return; // Dawn ignores it too
        event.preventDefault();
        event.stopImmediatePropagation();
        if (this.snapshotting) return;
        this.snapshotting = true;
        const submitter = event.submitter && event.submitter.form === this.form ? event.submitter : button;
        const variantId = this.form.querySelector('input[name="id"]')?.value || '';
        button.classList.add('loading');
        this.readCart().then((cart) => {
          this.snapshotting = false;
          button.classList.remove('loading'); // Dawn's handler sets its own loading state on the re-submit
          if (!this.active) return;
          this.pending = { cart, variantId, token: ++this.seq };
          this.releasing = true;
          try {
            if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(submitter);
            else this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
          } finally {
            this.releasing = false;
          }
        });
      }

      async onCartError(data) {
        const p = this.pending;
        if (!this.active || !p || !data || data.source !== 'product-form' || String(data.productVariantId) !== String(p.variantId)) return;
        this.pending = null;
        if (!p.cart) return; // no snapshot: keep Dawn's message
        const after = await this.readCart();
        if (!this.active || p.token !== this.seq || !after) return;
        const added = MdPdpController.units(after, p.variantId) - MdPdpController.units(p.cart, p.variantId);
        if (added <= 0) return; // nothing landed: Shopify's text is accurate
        const template = this.dataset.mdStockPartial || "Added {n}. That's all we have in stock right now.";
        const box = this.form.closest('product-form')?.querySelector('.product-form__error-message');
        const wrapper = box?.closest('.product-form__error-message-wrapper');
        if (box && this.form.closest('product-form')?.dataset.hideErrors !== 'true') {
          box.textContent = template.replace(/\{n\}/g, String(added));
          wrapper?.removeAttribute('hidden');
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'md-pdp', productVariantId: p.variantId, cartData: after });
        }
        this.refreshCartUi(after, !window.mdB2g1);
      }

      // Header bubble always; the drawer only when md-b2g1.js (which refreshes it on cartUpdate) is absent.
      async refreshCartUi(cart, withDrawer) {
        const drawer = document.querySelector('cart-drawer');
        const list = drawer && typeof drawer.getSectionsToRender === 'function' ? drawer.getSectionsToRender() : [{ id: 'cart-icon-bubble' }];
        const wanted = list.filter((s) => withDrawer || (s.section || s.id) !== 'cart-drawer');
        if (!wanted.length) return;
        try {
          const res = await fetch(`${this.cartUrl()}?sections=${wanted.map((s) => s.section || s.id).join(',')}`);
          const json = res.ok ? await res.json() : null;
          if (!json) return;
          wanted.forEach((s) => {
            const html = json[s.section || s.id];
            const target = s.selector ? document.querySelector(s.selector) : document.getElementById(s.id);
            if (typeof html !== 'string' || !target) return;
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const source = doc.querySelector(s.selector || '.shopify-section');
            if (source) target.innerHTML = source.innerHTML;
          });
          if (withDrawer && drawer) {
            const empty = !cart.item_count;
            drawer.classList.toggle('is-empty', empty);
            drawer.querySelector('.drawer__inner')?.classList.toggle('is-empty', empty);
          }
        } catch (e) {
          /* the next page load shows the cart */
        }
      }

      initSticky() {
        const bar = this.sticky;
        const button = this.form?.querySelector('[type="submit"]');
        if (!bar || !button) return;
        const setShown = (shown) => {
          bar.classList.toggle('is-shown', shown);
          bar.toggleAttribute('inert', !shown);
          if (shown) bar.removeAttribute('aria-hidden');
          else bar.setAttribute('aria-hidden', 'true');
        };
        setShown(false);
        // Shown only once the buy box button is entirely above the viewport, so the bar never covers the button
        // while any part of it is on screen (5.22a). A rAF-throttled scroll check rather than an
        // IntersectionObserver: an observer misses jumps that skip the button (anchor links, End key, restore).
        const update = () => {
          this.raf = 0;
          if (this.active) setShown(button.getBoundingClientRect().bottom < 0);
        };
        const schedule = () => {
          if (!this.raf) this.raf = requestAnimationFrame(update);
        };
        this.listen(window, 'scroll', schedule, { passive: true });
        this.listen(window, 'resize', schedule);
        this.cleanups.push(() => this.raf && cancelAnimationFrame(this.raf));
        update();
        this.listen(bar, 'click', (event) => {
          if (event.target.closest('[data-md-sticky-add]')) {
            const main = this.form.querySelector('[type="submit"]');
            if (!main || main.disabled || main.getAttribute('aria-disabled') === 'true') return;
            if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit(main);
            else main.click();
          } else if (event.target.closest('[data-md-sticky-cart]')) {
            const drawer = document.querySelector('cart-drawer');
            if (drawer && typeof drawer.open === 'function') drawer.open(event.target.closest('button'));
            else window.location.href = window.routes?.cart_url || '/cart';
          }
        });
      }
    }
  );
}

if (!customElements.get('md-ugc-rail')) {
  /* NATHAN-VIDEO-1: the whole video area is the play target (big centered Play glyph while not playing); state comes
     from the media events ('playing' sets is-playing and has-video, 'pause' clears is-playing), so a rejected play()
     (iOS Low Power Mode, an autoplay block) leaves the poster and the Play glyph in place and a tap still works; a
     shopper's pause is remembered so the loop observer never restarts it; on a loop already playing muted, the first
     tap (pointer) turns the sound on and keeps playing, the next tap pauses. Keyboard and screen readers keep the real
     Play button as a plain toggle (Enter or Space never unmutes). */
  let playing = null;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const canHls = () => document.createElement('video').canPlayType('application/vnd.apple.mpegurl') !== '';

  const attach = (video) => {
    if (video.dataset.attached) return;
    const src = canHls() && video.dataset.hls ? video.dataset.hls : video.dataset.mp4;
    if (!src) return;
    video.src = src;
    video.dataset.attached = '1';
  };
  const setPlaying = (card, on) => {
    card.classList.toggle('is-playing', on);
    card.querySelector('[data-md-play]')?.setAttribute('aria-pressed', on ? 'true' : 'false');
  };
  const syncSound = (card) => {
    const video = card.querySelector('video');
    // pressed = sound on (CR-P-14): one meaning, read from the element's real state
    card.querySelector('[data-md-mute]')?.setAttribute('aria-pressed', video && !video.muted ? 'true' : 'false');
  };
  const pause = (card, byUser) => {
    card.querySelector('video')?.pause();
    setPlaying(card, false);
    if (byUser) card.dataset.userPaused = '1';
    if (playing === card) playing = null;
  };
  const play = (card, byUser) => {
    const video = card.querySelector('video');
    if (!video) return;
    if (playing && playing !== card) pause(playing);
    if (byUser) delete card.dataset.userPaused;
    attach(video);
    playing = card;
    const p = video.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => {
        // blocked or interrupted: keep the poster and the Play glyph; the next tap is a user gesture and plays
        if (playing === card) playing = null;
        setPlaying(card, false);
        if (video.readyState < 2) card.classList.remove('has-video');
      });
    }
  };

  customElements.define(
    'md-ugc-rail',
    class MdUgcRail extends HTMLElement {
      connectedCallback() {
        this.observers = [];
        this.querySelectorAll('[data-md-video-card]').forEach((card) => this.initCard(card));
      }
      disconnectedCallback() {
        this.observers.forEach((io) => io.disconnect());
        this.querySelectorAll('[data-md-video-card]').forEach((card) => { if (playing === card) pause(card); });
      }
      initCard(card) {
        const video = card.querySelector('video');
        if (!video) return;
        if (video.hasAttribute('muted')) video.muted = true; // the attribute is the server's starting state
        syncSound(card);
        const activate = (byPointer) => {
          const on = card.classList.contains('is-playing');
          const soundClip = !!card.querySelector('[data-md-mute]');
          if (on && byPointer && video.muted && soundClip && card.hasAttribute('data-md-loop') && !card.dataset.soundTap) {
            video.muted = false; // first tap on a playing muted loop: sound on, keep playing
            card.dataset.soundTap = '1';
            syncSound(card);
            return;
          }
          if (on) pause(card, true);
          else play(card, true);
        };
        // event.detail is 0 for keyboard activation (Enter or Space): a plain toggle there
        card.querySelector('[data-md-play]')?.addEventListener('click', (event) => activate(event.detail > 0));
        card.querySelector('.md-ugc-card__media')?.addEventListener('click', (event) => {
          if (event.target.closest('button, a, summary, input, label')) return;
          activate(true);
        });
        card.querySelector('[data-md-mute]')?.addEventListener('click', () => {
          video.muted = !video.muted;
          syncSound(card);
        });
        const cc = card.querySelector('[data-md-captions]');
        cc?.addEventListener('click', () => {
          const track = video.textTracks[0];
          if (!track) return;
          const on = track.mode !== 'showing';
          track.mode = on ? 'showing' : 'hidden';
          cc.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        video.addEventListener('playing', () => {
          if (playing && playing !== card) pause(playing);
          playing = card;
          setPlaying(card, true);
          card.classList.add('has-video');
        });
        video.addEventListener('pause', () => setPlaying(card, false));
        video.addEventListener('ended', () => setPlaying(card, false));
        video.addEventListener('volumechange', () => syncSound(card));
        if (card.hasAttribute('data-md-loop') && !reduceMotion.matches && !navigator.connection?.saveData) {
          const io = new IntersectionObserver(
            ([entry]) => {
              if (entry.intersectionRatio >= 0.5) {
                if (!card.dataset.userPaused && (!playing || playing === card) && !card.classList.contains('is-playing')) play(card);
              } else if (card.classList.contains('is-playing')) pause(card);
            },
            { threshold: [0, 0.5] }
          );
          io.observe(card);
          this.observers.push(io);
        }
      }
    }
  );
}

/* NATHAN-PDP-MOBILE-3: gallery edge tone (see the header). Phones only; desktop keeps the studio gray. */
(() => {
  if (window.mdPdpEdgeTone) return;
  const phone = window.matchMedia('(max-width: 749px)');
  const tone = (img) => {
    const media = img.closest('.media');
    if (!media || img.classList.contains('md-media-fill') || !img.naturalWidth || media.style.getPropertyValue('--md-edge')) return;
    try {
      const w = 8, h = 24;
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, w, h);
      const cols = [ctx.getImageData(0, 0, 1, h).data, ctx.getImageData(w - 1, 0, 1, h).data];
      const px = [];
      cols.forEach((d) => { for (let i = 0; i < d.length; i += 4) if (d[i + 3] > 250) px.push([d[i], d[i + 1], d[i + 2]]); });
      if (px.length < h) return; // mostly transparent edges: leave the token
      // median per channel; a studio edge is flat apart from a few shadow rows, so 75 percent must sit within 6 of it
      const median = [0, 1, 2].map((c) => px.map((p) => p[c]).sort((x, y) => x - y)[px.length >> 1]);
      const near = px.filter((p) => [0, 1, 2].every((c) => Math.abs(p[c] - median[c]) <= 6)).length;
      if (near < px.length * 0.75) return; // not a flat studio edge (a lifestyle slide or chart): leave the token
      media.style.setProperty('--md-edge', `rgb(${median.join(' ')})`);
    } catch (e) {
      /* tainted canvas (cross-origin image): the CSS token stays */
    }
  };
  const run = () => {
    if (!phone.matches) return;
    document.querySelectorAll('.md-pdp .product__media-item .media > img').forEach((img) => {
      if (img.complete) tone(img);
      else img.addEventListener('load', () => tone(img), { once: true });
    });
  };
  window.mdPdpEdgeTone = { tone, run };
  run();
  phone.addEventListener?.('change', run);
  document.addEventListener('shopify:section:load', run);
})();

/* NATHAN-MOBILE-UX-1: compare table collapsed on phones behind one toggle; reviews capped with Read all reviews. */
(() => {
  if (window.mdPdpDensity) return;
  const initCompare = (root) => {
    const btn = root.querySelector('[data-md-compare-toggle]');
    if (!btn || root.classList.contains('is-collapsible')) return;
    root.classList.add('is-collapsible');
    btn.hidden = false;
    btn.addEventListener('click', () => {
      const open = btn.getAttribute('aria-expanded') !== 'true';
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      root.classList.toggle('is-open', open);
    });
  };
  const initReviews = (root) => {
    if (root.dataset.mdReviewsReady) return;
    root.dataset.mdReviewsReady = '1';
    const body = root.querySelector('.md-pdp-reviews__body');
    const more = root.querySelector('.md-pdp-reviews__more');
    const btn = root.querySelector('[data-md-reviews-more]');
    if (!body || !more || !btn) return;
    const capOf = () => parseFloat(getComputedStyle(root).getPropertyValue(window.matchMedia('(min-width: 750px)').matches ? '--md-reviews-cap-wide' : '--md-reviews-cap')) || 640;
    const measure = () => {
      if (root.classList.contains('is-open')) return;
      const tall = body.scrollHeight > capOf() + 80; // only cap when there is a meaningful amount to hide
      root.classList.toggle('is-capped', tall);
      more.hidden = !tall;
    };
    const open = () => {
      root.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
      more.hidden = true;
    };
    btn.addEventListener('click', () => {
      open();
      body.setAttribute('tabindex', '-1');
      body.focus({ preventScroll: true });
    });
    // the star widget and other links jump to the reviews anchor: open first so the target is reachable
    const openForHash = () => {
      const id = decodeURIComponent((location.hash || '').slice(1));
      if (id && body.querySelector('#' + CSS.escape(id))) open();
    };
    document.addEventListener('click', (e) => {
      const a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      const id = decodeURIComponent(a.getAttribute('href').slice(1));
      if (id && (body.querySelector('#' + CSS.escape(id)) || /review/i.test(id))) open();
    });
    window.addEventListener('hashchange', openForHash);
    // measure in the next frame: toggling the cap resizes the observed box, which would otherwise loop (console error)
    if (typeof ResizeObserver === 'function') new ResizeObserver(() => requestAnimationFrame(measure)).observe(body);
    measure();
    openForHash();
  };
  const run = () => {
    document.querySelectorAll('[data-md-compare]').forEach(initCompare);
    document.querySelectorAll('[data-md-reviews]').forEach(initReviews);
  };
  window.mdPdpDensity = { run };
  run();
  document.addEventListener('shopify:section:load', run);
})();

